import { faEyeSlash } from "@fortawesome/duotone-regular-svg-icons/faEyeSlash";
import { faMagnifyingGlassMinus } from "@fortawesome/duotone-regular-svg-icons/faMagnifyingGlassMinus";
import { faMap } from "@fortawesome/duotone-regular-svg-icons/faMap";
import { faSadCry } from "@fortawesome/duotone-regular-svg-icons/faSadCry";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AppShell,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Paper,
  Portal,
  Skeleton,
  Stack,
  Text,
} from "@mantine/core";
import { MapGeoJSONFeature } from "maplibre-gl";
import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetRef } from "react-modal-sheet";
import styled from "styled-components";
import { VList } from "virtua";
import { ROUTES } from "../../constants/routes";
import { useIsMediumScreen } from "../../hooks/breakpoints";
import {
  setCampgroundsVisible,
  useCampgroundsVisible,
} from "../../store/slices/campgroundLayer";
import {
  useCampgrounds,
  useIsInitialLoad,
  useQuery,
  useStatus,
  useVisibleCampgrounds,
} from "../../store/slices/campgroundSearch";
import { useAppDispatch } from "../../store/store";
import useIsRoute from "../../utils/useIsRoute";
import CampgroundCard from "../campground-card/CampgroundCard";
import useZoomToIsochrone from "../map/useZoomToIsochrone";

const MantineSheet = styled(Sheet)`
  .react-modal-sheet-container {
    background-color: var(--mantine-color-body) !important;
    border-top-right-radius: var(--mantine-radius-lg) !important;
    border-top-left-radius: var(--mantine-radius-lg) !important;
  }
`;

// Create a memoized ResultsSuccessList component that holds the auto-sizing list of campgrounds
// This component will be used in the ResultsSuccess component
const ResultsSuccessList = React.memo(() => {
  const visibleCampgrounds = useVisibleCampgrounds() as
    | MapGeoJSONFeature[]
    | undefined;
  if (!visibleCampgrounds) return null;

  return (
    <VList style={{ padding: "0px 10px 10px 10px" }} bufferSize={10}>
      {visibleCampgrounds.map((campground, index) => {
        if (!campground.properties) return null;
        return (
          <div
            key={campground.properties.id}
            style={{
              paddingTop: index == 0 ? 10 : 0,
              paddingBottom: index + 1 == visibleCampgrounds.length ? 0 : 10,
            }}
          >
            <CampgroundCard campground={campground} />
          </div>
        );
      })}
    </VList>
  );
});

const ResultsSuccess = () => {
  const campgrounds = useCampgrounds();
  const visibleCampgrounds = useVisibleCampgrounds();
  const campgroundsVisible = useCampgroundsVisible();
  const dispatch = useAppDispatch();

  const zoomToIsochrone = useZoomToIsochrone();
  const isSmallScreen = useIsMediumScreen();
  const resultsDivRef = useRef<HTMLDivElement>(null);
  const isExploring = useIsRoute(ROUTES.EXPLORE);

  const ref = useRef<SheetRef>(null);
  const snapTo = (i: number) => ref.current?.snapTo(i);

  // Show alert when campgrounds are hidden but there are results
  const showHiddenAlert =
    !campgroundsVisible && (campgrounds?.features.length ?? 0) > 0;

  let ComponentToRender = <ResultsSuccessList />;

  if (showHiddenAlert) {
    // Desktop version - mobile is handled as a banner above the sheet
    ComponentToRender = (
      <Box
        display="flex"
        style={{ justifyContent: "center", alignItems: "center", flex: 1 }}
      >
        <Stack gap="md" justify="center" align="center" p="md" maw={400}>
          <Group justify="center" c="yellow">
            <FontAwesomeIcon icon={faEyeSlash} size="3x" />
          </Group>
          <Text ta="center" fw={600} size="lg">
            Campgrounds hidden on map
          </Text>
          <Text ta="center" c="dimmed" size="sm">
            The campground layer is currently hidden. Enable it to see your{" "}
            {campgrounds?.features.length} search results on the map.
          </Text>
          <Button
            color="yellow"
            onClick={() => dispatch(setCampgroundsVisible(true))}
          >
            Show Campgrounds
          </Button>
        </Stack>
      </Box>
    );
  } else if (campgrounds?.features.length === 0) {
    ComponentToRender = (
      <Box
        display="flex"
        style={{ justifyContent: "center", alignItems: "center", flex: 1 }}
      >
        <Stack gap="md" justify="center" align="center" p="10px">
          <Group justify="center" c="loaderColor">
            <FontAwesomeIcon icon={faSadCry} size="3x" />
          </Group>
          <Text ta="center">No campgrounds found </Text>
        </Stack>
      </Box>
    );
  } else if (visibleCampgrounds == undefined) {
    // Still loading campgrounds. This occurs after the results have been fetched but before
    // they're displayed on the map.
    ComponentToRender = <ResultsLoading />;
  } else if (visibleCampgrounds.length === 0) {
    ComponentToRender = (
      <Box
        display="flex"
        style={{ justifyContent: "center", alignItems: "center", flex: 1 }}
      >
        <Stack gap="md" justify="center" align="center" p="10px">
          <Group justify="center" c="loaderColor">
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} size="3x" />
            <FontAwesomeIcon icon={faMap} size="3x" />
          </Group>
          <Text ta="center">
            Zoom out or move the map to see more campgrounds
          </Text>
          {!isExploring && (
            <Button variant="subtle" w="fit-content" onClick={zoomToIsochrone}>
              i'm lost (do it for me)
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  if (isSmallScreen) {
    let sheetTitle = "Searching for campgrounds...";
    if (
      campgrounds?.features.length !== undefined &&
      visibleCampgrounds !== undefined
    ) {
      sheetTitle = `Found ${campgrounds?.features.length} campgrounds, displaying ${visibleCampgrounds.length}`;
    }

    return (
      <>
        {/* Show hidden campgrounds alert as a banner */}
        {showHiddenAlert && (
          <Paper
            style={{
              position: "absolute",
              bottom: 10,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              width: "calc(100vw - 20px)",
              maxWidth: 400,
            }}
            shadow="lg"
            p="sm"
          >
            <Stack gap="xs" align="center">
              <Group gap="xs" c="yellow">
                <FontAwesomeIcon icon={faEyeSlash} />
                <Text fw={600} size="sm">
                  Campgrounds hidden on map
                </Text>
              </Group>
              <Button
                fullWidth
                size="sm"
                color="yellow"
                onClick={() => dispatch(setCampgroundsVisible(true))}
              >
                Show Campgrounds
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Only show sheet when campgrounds are visible */}
        {!showHiddenAlert && (
          <MantineSheet
            snapPoints={[0.0, 0.1, 0.25, 0.5, 1.0]}
            isOpen
            initialSnap={2}
            onClose={() => snapTo(2)}
            disableDismiss
            ref={ref}
            style={{ zIndex: 199 }}
            className="search-results-drawer"
            avoidKeyboard
            disableScrollLocking
          >
            <Sheet.Container>
              <Sheet.Header>
                <Center>
                  <Stack align="center" gap={"0.3rem"} mb="0.3rem">
                    <Divider w={"10%"} mt="10px" size="md" />
                    <Text c="dimmed">{sheetTitle}</Text>
                  </Stack>
                </Center>
              </Sheet.Header>
              <Sheet.Content
                style={{ display: "flex", flexDirection: "column" }}
                disableDrag
              >
                <Box
                  display="flex"
                  ref={resultsDivRef}
                  h={"100%"}
                  mt={!isSmallScreen ? "10px" : 0}
                >
                  {ComponentToRender}
                </Box>
                {!isSmallScreen && (
                  <AppShell.Section>
                    <Text c="dimmed" size="sm" ta="center">
                      {sheetTitle}
                    </Text>
                  </AppShell.Section>
                )}
              </Sheet.Content>
            </Sheet.Container>
          </MantineSheet>
        )}
      </>
    );
  } else {
    return ComponentToRender;
  }
};

const ResultsLoading = () => {
  const [skeletonCount, setSkeletonCount] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const containerHeight = sectionRef.current?.clientHeight || 0;
      const count = Math.ceil(containerHeight / 65);
      setSkeletonCount(count);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <AppShell.Section ref={sectionRef} grow style={{ padding: 10 }}>
      <Stack gap="md">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <Skeleton key={index} animate height={65} />
        ))}
      </Stack>
    </AppShell.Section>
  );
};

const SearchResults = () => {
  const status = useStatus();
  const query = useQuery();
  const isInitialLoad = useIsInitialLoad();
  const isSmallScreen = useIsMediumScreen();

  const searchResultComponent = <ResultsSuccess />;

  if (!query || isInitialLoad) return null;

  if (isSmallScreen) {
    return (
      <Portal>
        <Paper
          style={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            position: "absolute",
            bottom: 0,
            zIndex: 10,
            width: "100vw",
            height: status === "success" ? "fit-content" : 0,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          }}
          radius={"lg"}
        >
          {searchResultComponent}
        </Paper>
      </Portal>
    );
  } else {
    return (
      <Box flex={1} display="flex" w={"400px"} style={{ overflow: "hidden" }}>
        {searchResultComponent}
      </Box>
    );
  }
};

export default SearchResults;
